import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { IconButton } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { StandaloneQuestion } from './QuestionsAndAnswers.types';

type FormData = {
	content: string;
};

type Props = {
	question: StandaloneQuestion;
	/** open the answer form by default (the Unanswered view) */
	defaultOpen: boolean;
	/** called after any successful mutation so the list refetches */
	onChanged: () => void;
};

// local stand-in for the instructor app's ContentConfig icon map
// (@eduact/utils is not part of the admin build)
const UNIT_TYPE_ICONS: Record<string, string> = {
	video: 'play_circle',
	document: 'description',
	webinar: 'videocam',
	webcontent: 'language',
	test: 'quiz',
	assignment: 'assignment',
};

const StandaloneQuestionCard: React.FC<Props> = ({
	question,
	defaultOpen,
	onChanged,
}) => {
	const { displaySnackbar } = useSnackbar();
	const [open, setOpen] = useState(defaultOpen);
	const [confirmDelete, setConfirmDelete] = useState(false);
	// a solved question always has its answer as the first reply — but guard:
	// a reply deleted straight in the DB must not crash the whole list
	const existingAnswer = question.solved ? question.replies?.[0] : undefined;

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<FormData>({
		mode: 'onTouched',
		defaultValues: {
			content: existingAnswer?.content ?? '',
		},
	});

	const onSubmit = async ({ content }: FormData) => {
		if (!content?.trim()) return;
		try {
			if (existingAnswer) {
				await api.post('discussions/replies/update', {
					reply_id: existingAnswer.id,
					content,
				});
				displaySnackbar('success', 'Answer updated successfully');
			} else {
				await api.post('discussions/replies/create', {
					discussion_id: question.id,
					content,
				});
				displaySnackbar('success', 'Question answered successfully');
			}
			onChanged();
		} catch (error: any) {
			// "Question already answered" (someone else got there first) and
			// ownership refusals both land here
			displaySnackbar(
				'error',
				error?.response?.data?.message ??
					(existingAnswer ? 'Could not update the answer' : 'Could not answer the question')
			);
		}
	};

	const handleDelete = async (_close: boolean, confirm?: boolean) => {
		setConfirmDelete(false);
		if (!confirm) return;
		try {
			await api.post('discussions/delete', { discussion_id: question.id });
			displaySnackbar('success', 'Question deleted successfully');
			onChanged();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Could not delete the question'
			);
		}
	};

	const unitTypeName = question.unit?.unitType?.name ?? '';

	return (
		<Container>
			<EdConfirmModal
				open={confirmDelete}
				handleClose={handleDelete}
				text="Are you sure you want to delete this question?"
			/>
			<CornerActions>
				<IconButton size="small" onClick={() => setOpen((prev) => !prev)} aria-label="toggle answer">
					<ToggleIcon isOpen={open}>expand_more</ToggleIcon>
				</IconButton>
			</CornerActions>
			<DeleteAction>
				<IconButton size="small" onClick={() => setConfirmDelete(true)} aria-label="delete question">
					<EdIcon>delete</EdIcon>
				</IconButton>
			</DeleteAction>
			<QuestionSectionContainer>
				<UserInfoContainer>
					<Username>{question.user.username}</Username>
					<FullName>{`${question.user.first_name ?? ''} ${question.user.last_name ?? ''}`}</FullName>
				</UserInfoContainer>
				<QuestionText>{question.content}</QuestionText>
				<TimeText>
					{moment(question.updated_at).format('DD/MM/YYYY hh:mm A')}
				</TimeText>
				{open && (
					<form onSubmit={handleSubmit(onSubmit)}>
						<AnswerForm>
							<AnswerInput
								placeholder="type your answer here"
								isInvalid={!!errors.content}
								{...register('content', { required: true })}
							/>
							<SubmitButton disabled={isSubmitting} type="submit">
								{existingAnswer ? 'Update' : 'Submit'}
							</SubmitButton>
						</AnswerForm>
					</form>
				)}
			</QuestionSectionContainer>

			<Divider />

			<QuestionInfoSection>
				<CourseName>{question.course?.name ?? '-'}</CourseName>
				<Unit>
					<EdIcon>{UNIT_TYPE_ICONS[unitTypeName] ?? 'help_outline'}</EdIcon>
					<UnitType>{question.unit?.name ?? '-'}</UnitType>
				</Unit>
			</QuestionInfoSection>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	display: flex;
	gap: 18px;
	margin: 16px 0;
	padding: 20px 32px;
	background-color: white;
	box-shadow: 0 3px 6px #00000029;
	border: 1px solid #f5f5f5;
	border-radius: 5px;
`;

const CornerActions = styled.div`
	position: absolute;
	top: 8px;
	right: 8px;
`;

const ToggleIcon = styled(EdIcon)<{ isOpen?: boolean }>`
	transform: ${(props) => (props.isOpen ? 'rotateX(0.5turn)' : 'rotateX(0turn)')};
`;

const DeleteAction = styled.div`
	position: absolute;
	bottom: 8px;
	right: 8px;
`;

const UserInfoContainer = styled.div`
	display: flex;
	gap: 32px;
	align-items: center;
`;

const Username = styled.p`
	font-size: 18px;
	font-weight: 600;
	letter-spacing: 0.14px;
	color: #251b33;
`;

const FullName = styled.p`
	letter-spacing: 0.12px;
	color: #a4a4a4;
`;

const QuestionText = styled.p`
	margin-top: 18px;
	font-weight: 600;
	letter-spacing: 0.12px;
	color: #2f2e41;
`;

const TimeText = styled.p`
	letter-spacing: 0.09px;
	color: #a4a4a4;
	margin-left: auto;
	margin-bottom: 10px;
	margin-top: 10px;
	width: fit-content;
`;

const AnswerForm = styled.div`
	display: flex;
	gap: 14px;
`;

const AnswerInput = styled.input<{ isInvalid: boolean }>`
	width: 100%;
	border: 1px solid ${(props) => (props.isInvalid ? 'red' : '#C4C4C4')};
	border-radius: 5px;
	font-size: 16px;
	padding: 8px 12px;
`;

const SubmitButton = styled.button`
	text-transform: uppercase;
	min-width: 68px;
	background-color: transparent;
	border: 1px solid #ff9800;
	color: #ff9800;
	border-radius: 6px;
	cursor: pointer;

	:hover {
		background-color: #ff9800;
		color: white;
	}

	:disabled {
		cursor: not-allowed;
		opacity: 0.65;
	}
`;

const QuestionSectionContainer = styled.div`
	width: 100%;
`;

const Divider = styled.div`
	border-left: 1px solid #c4c4c4;
	margin-top: 16px;
`;

const QuestionInfoSection = styled.div`
	width: 300px;
	margin-top: 16px;
`;

const CourseName = styled.p`
	letter-spacing: 0.12px;
	color: #251b33;
	font-weight: 600;
`;

const Unit = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 10px;
	color: #a4a4a4;
`;

const UnitType = styled.p`
	font-size: 14px;
	letter-spacing: 0.11px;
`;

export default StandaloneQuestionCard;
